// Externals imports.
import React, { createContext, ReactElement } from 'react'
import { Subject } from 'rxjs'
import { SnackbarProvider, useSnackbar } from '../../../Vendor/notistack'
import { PlaceInAkixiNameSpace } from '../../../NonReact/utils/Utils'
import MoreDetailsType from '../Components/SnackbarMessage/Types'
import { messageType } from '../Types/Types'
import {
    displayError,
    displayErrorMessage,
    displayInfoMessage,
    displaySuccessMessage,
    displayWarningMessage
} from '../Utils/Messages'

/**
 * Message context type.
 */
export type MessageContextType = {
    displayError: (pError: Error) => void
    displayErrorMessage: (
        message: messageType,
        details?: MoreDetailsType,
        internalCloseTrigger?: Subject<void>
    ) => Voidable<boolean>
    displayInfoMessage: (
        message: messageType,
        details?: MoreDetailsType,
        internalCloseTrigger?: Subject<void>
    ) => Voidable<boolean>
    displayWarningMessage: (
        message: messageType,
        details?: MoreDetailsType,
        internalCloseTrigger?: Subject<void>
    ) => Voidable<boolean>
    displaySuccessMessage: (
        message: messageType,
        details?: MoreDetailsType,
        internalCloseTrigger?: Subject<void>
    ) => Voidable<boolean>
}

/**
 * Snackbar controller properties type.
 */
type SnackBarController = {
    children: React.ReactNode
}

/**
 * Provider properties type.
 */
type ProviderProps = {
    children: React.ReactNode
}

/**
 * Provides snackbar messages access.
 * @param children Children to render.
 */
const SnackbarController = ({ children }: SnackBarController): ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!com.Akixi.enqueueSnackbar) {
        const { enqueueSnackbar, closeSnackbar } = useSnackbar()

        PlaceInAkixiNameSpace('enqueueSnackbar', enqueueSnackbar)
        PlaceInAkixiNameSpace('closeSnackbar', closeSnackbar)
    }

    return (
        <MessageContext.Provider
            value={{
                displayError,
                displayErrorMessage,
                displayInfoMessage,
                displayWarningMessage,
                displaySuccessMessage
            }}
        >
            {children}
            {/* Place the message snackbar. */}
        </MessageContext.Provider>
    )
}

/**
 * Message context provider.
 * @param children Children to render.
 */
export const MessageContextProvider = ({ children }: ProviderProps): ReactElement => (
    <SnackbarProvider
        classes={{ containerAnchorOriginBottomLeft: 'snack-container' }}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
        }}
        domRoot={document.body}
    >
        <SnackbarController>{children}</SnackbarController>
    </SnackbarProvider>
)

/**
 * Context for messages.
 */
// eslint-disable-next-line
const MessageContext = createContext<MessageContextType>(undefined as any)
export default MessageContext
